/**
 * Transform API slide data into modal-ready content format
 * Handles filtering and video URL extraction from Discovery Education API responses
 */

/**
 * Extracts the best available video URL from media files array
 * Prioritizes MP4 format for maximum browser compatibility
 *
 * @param {Array} mediaFiles - Array of media file objects from API
 * @returns {string} Video URL or empty string if none found
 */
const extractVideoUrl = (mediaFiles) => {
    if (!mediaFiles || !Array.isArray(mediaFiles) || mediaFiles.length === 0) {
        return '';
    }

    // Try MP4 format first (best browser compatibility)
    const mp4File = mediaFiles.find((file) => file.format?.name?.toLowerCase().includes('mp4') || file.url?.toLowerCase().includes('.mp4'));

    // Fallback to any video file
    const anyVideoFile = mediaFiles.find(
        (file) => file.url && (file.url.includes('.webm') || file.url.includes('video') || file.format?.name?.toLowerCase().includes('video')),
    );

    return mp4File?.url || anyVideoFile?.url || mediaFiles[0]?.url || '';
};

/**
 * Checks if a slide should be included in the modal
 * Filters out slides with errors or missing video content
 *
 * @param {Object} slide - Slide object from API
 * @returns {boolean} True if slide should be included
 */
const shouldIncludeSlide = (slide) => {
    // Filter out slides with errors (permission denied, etc.)
    if (slide.error) {
        return false;
    }

    // Check if slide has video content
    const asset = slide.data?.asset;
    const hasMediaFiles = asset?.media_files && Array.isArray(asset.media_files) && asset.media_files.length > 0;

    return hasMediaFiles;
};

/**
 * Transforms a single slide from API format to modal content format
 *
 * @param {Object} slide - Raw slide data from API
 * @returns {Object} Transformed slide object
 */
const transformSlide = (slide) => {
    const asset = slide.data?.asset;

    const videoUrl = extractVideoUrl(asset?.media_files);

    return {
        title: asset?.title || '',
        subtitle: asset?.subtitle || '',
        listItemTitle: asset?.title || '',
        description: asset?.description || '',
        videoGuid: slide.videoGuid || '',
        videoUrl,
        moreLinkText: 'Learn more',
        moreLinkUrl: asset?.url || '',
    };
};

/**
 * Transforms raw API slides data into modal-ready content structure
 *
 * @param {Array} slides - Array of slide objects from API
 * @returns {Object} Modal content with title and transformed slides array
 *
 * @example
 * const modalContent = transformSlidesToModalContent(apiSlides);
 * // Returns: { title: 'Career Connect', slides: [...] }
 */
export const transformSlidesToModalContent = (slides) => {
    if (!slides || !slides.length) {
        return { title: 'Career Connect', slides: [] };
    }

    const transformedSlides = slides.filter(shouldIncludeSlide).map(transformSlide);

    return {
        title: 'Career Connect',
        slides: transformedSlides,
    };
};

export default transformSlidesToModalContent;
